import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { Store } from '@ngxs/store';
import { combineLatest, defer, delay, map, of, switchMap, take } from 'rxjs';

import { ClearCustomerAction } from '@app/core/store/customer/customer.actions';
import { CustomerState } from '@app/core/store/customer/customer.state';
import { RemoveStudentSessionAction } from '@app/features/student/store/student-session/student-session.action';
import { StudentSessionState } from '@app/features/student/store/student-session/student-session.state';

const SEARCH_PATH = '/buscar-aluno';
const BOOK_PATH = '/livro-do-aluno';

function handleAccessTokenMismatch(store: Store) {
  return store
    .dispatch([
      new RemoveStudentSessionAction({ redirect: 'none' }),
      new ClearCustomerAction()
    ])
    .pipe(
      delay(150),
      map(() => true)
    );
}

function resolveStoreGuard(
  route: Parameters<CanActivateFn>[0],
  state: Parameters<CanActivateFn>[1],
  store: Store,
  router: Router
) {
  const url = state.url;
  const isSearchPage = url.startsWith(SEARCH_PATH);
  const hasAccessToken = route.queryParamMap?.has('accessToken');

  return combineLatest([
    store.select(StudentSessionState.token),
    store.select(CustomerState.customer)
  ]).pipe(
    take(1),
    switchMap(([token, customer]) => {
      const hasToken = !!token;
      const hasCustomerId = !!customer?.id;

      if (hasAccessToken && hasToken && hasCustomerId) {
        const accessTokenParam = route.queryParamMap.get('accessToken') || '';
        const pass = store.selectSnapshot(StudentSessionState.pass);
        const same = accessTokenParam === pass;

        return defer(() =>
          same
            ? of(router.createUrlTree([BOOK_PATH]))
            : handleAccessTokenMismatch(store)
        );
      }

      if (hasToken && hasCustomerId) {
        return of(isSearchPage ? router.createUrlTree([BOOK_PATH]) : true);
      }

      if (!hasToken || !hasCustomerId) {
        return of(isSearchPage ? true : router.createUrlTree([SEARCH_PATH]));
      }

      return of(true);
    })
  );
}

export const storeGuard: CanActivateFn = (route, state) => {
  const store = inject(Store);
  const router = inject(Router);
  return resolveStoreGuard(route, state, store, router);
};
